import { useEffect, useState } from "react";
import { DateView } from "nystem-components";
import app from "nystem";

const ViewButtonEnergyHistory = () => {
  const [consumption, setConsumption] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [start, setStart] = useState(() => new Date().setHours(0, 0, 0, 0));

  useEffect(() => {
    let mounted = true;
    let timer;
    setConsumption(null);
    setError("");
    const update = async () => {
      const start = new Date().setHours(0, 0, 0, 0);
      setStart(start);
      try {
        const { consumption, error } = await app.connection.emit({
          type: "energyConsumption",
          start,
          end: new Date(start).setDate(new Date(start).getDate() + 1),
        });
        if (mounted) {
          setConsumption(error ? null : consumption);
          setError(error || "");
        }
      } finally {
        if (mounted) timer = setTimeout(update, 3600000);
      }
    };
    update();
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, []);

  const highest = Math.max(0.01, ...(consumption || []).map(({ value }) => value));
  const magnitude = 10 ** Math.floor(Math.log10(highest / 4));
  const step = [1, 2, 2.5, 5, 10].find((value) => value * magnitude >= highest / 4) * magnitude;
  const maximum = Math.ceil(highest / step) * step;
  const ticks = Array.from({ length: Math.round(maximum / step) + 1 }, (_, pos) => pos * step);
  const active = consumption?.find(({ time }) => time === selected);
  const end = new Date(start).setDate(new Date(start).getDate() + 1);
  return (
    <section aria-label="Hourly electricity consumption">
      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-400">
        <span className="shrink-0">Today by hour · kWh</span>
        {consumption ? (
          <span className="ml-auto flex items-center gap-1 text-right tabular-nums">
            {active && (
              <>
                <DateView model={{ dateFormat: "time", renderAs: "span" }} value={active.time} /> ·{" "}
              </>
            )}
            {(active ? active.value : consumption.reduce((sum, { value }) => sum + value, 0)).toFixed(2)} kWh
          </span>
        ) : (
          <span className="ml-auto text-right">{error || "Loading today’s Shelly history…"}</span>
        )}
      </div>
      {consumption ? (
        <>
          <div className="relative mt-4 pr-14 pl-12">
            <div className="relative h-6 text-xs leading-none text-gray-300 tabular-nums">
              {ticks.map((value) => (
                <span key={value} className="absolute -translate-x-1/2" style={{ left: `${(value / maximum) * 100}%` }}>
                  {Number(value.toPrecision(12))}
                </span>
              ))}
            </div>
            <div className="relative h-[30rem]">
              {ticks.map((value) => (
                <div
                  key={value}
                  className="pointer-events-none absolute top-0 bottom-0 border-l border-gray-500/60"
                  style={{ left: `${(value / maximum) * 100}%` }}
                />
              ))}
              {Array.from({ length: Math.round((end - start) / 3600000) }, (_, hour) => {
                const time = start + hour * 3600000;
                const reading = consumption.find((reading) => reading.time === time);
                return (
                  <button
                    key={time}
                    type="button"
                    disabled={!reading}
                    aria-label={
                      reading ? `${reading.value.toFixed(2)} kWh, hour ${hour + 1}` : "No recorded consumption"
                    }
                    className={`absolute left-0 rounded-r-sm focus:outline-2 focus:outline-white ${reading ? (selected === time ? "bg-emerald-300" : "bg-emerald-500/80 hover:bg-emerald-300") : "bg-gray-700/40"}`}
                    style={{
                      top: `${((time - start) / (end - start)) * 100}%`,
                      width: reading ? `${Math.max(0.5, (reading.value / maximum) * 100)}%` : "2px",
                      height: `calc(${(3600000 / (end - start)) * 100}% - 3px)`,
                    }}
                    onMouseEnter={() => setSelected(time)}
                    onFocus={() => setSelected(time)}
                    onClick={() => setSelected(time)}
                  />
                );
              })}
            </div>
            <div className="pointer-events-none absolute top-6 right-0 left-0 h-[30rem] text-xs text-gray-300 tabular-nums">
              {Array.from({ length: Math.round((end - start) / 3600000) }, (_, hour) => {
                const time = start + hour * 3600000;
                const reading = consumption.find((reading) => reading.time === time);
                return (
                  <div
                    key={time}
                    className="absolute right-0 left-0 flex -translate-y-1/2 justify-between"
                    style={{ top: `${((time - start + 1800000) / (end - start)) * 100}%` }}
                  >
                    <DateView model={{ dateFormat: "time", renderAs: "span" }} value={time} />
                    <span className="w-12 text-right">{reading ? reading.value.toFixed(2) : "—"}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="mt-1 text-[10px] text-gray-500">Recorded consumption · Current hour may be incomplete.</p>
        </>
      ) : null}
    </section>
  );
};

export default ViewButtonEnergyHistory;
